import { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth, ALL_PERMISSIONS } from '../hooks/useAuth';
import api from '../utils/api';
import { Plus, Edit2, Trash2, X, Eye, EyeOff, Shield, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLES = [
  { v:'admin',       l:'Administrator', badge:'badge-red',   desc:'Alle Rechte' },
  { v:'benutzer',    l:'Benutzer',      badge:'badge-blue',  desc:'Standard' },
  { v:'mitarbeiter', l:'Mitarbeiter',   badge:'badge-green', desc:'Eingeschränkt' },
];

// Standard-Permissions je Rolle (für Reset-Button)
const ROLE_DEFAULTS = {
  admin:       ALL_PERMISSIONS.map(p => p.v),
  benutzer:    ['dashboard','fuhrpark','kalender','schaden','packliste','einstellungen'],
  mitarbeiter: ['dashboard','kalender','schaden','einstellungen'],
};

const EMPTY = { name:'', email:'', password:'', role:'benutzer', permissions: ROLE_DEFAULTS.benutzer };

// Permissions nach Gruppe
const PERM_GROUPS = ALL_PERMISSIONS.reduce((acc, p) => {
  if (!acc[p.group]) acc[p.group] = [];
  acc[p.group].push(p);
  return acc;
}, {});

function BottomSheet({ title, onClose, children, footer }) {
  return (
    <div
      style={{ position:'fixed',inset:0,zIndex:200,background:'rgba(0,0,0,.5)',backdropFilter:'blur(4px)',WebkitBackdropFilter:'blur(4px)',display:'flex',alignItems:'flex-end',justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'var(--bg-card)',borderRadius:'20px 20px 0 0',width:'100%',maxWidth:600,maxHeight:'94dvh',display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <div style={{ width:36,height:4,background:'var(--border)',borderRadius:2,margin:'10px auto 0',flexShrink:0 }}/>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',borderBottom:'1px solid var(--border)',flexShrink:0 }}>
          <span style={{ fontWeight:700,fontSize:'1rem' }}>{title}</span>
          <button onClick={onClose} style={{ display:'flex',alignItems:'center',padding:7,border:'none',background:'var(--bg)',cursor:'pointer',borderRadius:8,color:'var(--text)' }}><X size={16}/></button>
        </div>
        <div style={{ flex:1,overflowY:'auto',padding:'16px 18px' }}>{children}</div>
        {footer && <div style={{ padding:'14px 18px',borderTop:'1px solid var(--border)',display:'flex',gap:10,flexShrink:0 }}>{footer}</div>}
      </div>
    </div>
  );
}

export default function Benutzer() {
  const { user: me } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [showPw, setShowPw]   = useState(false);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/users')
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const f = (k, v) => setForm(p => ({...p, [k]: v}));

  const togglePerm = (perm) => {
    setForm(p => ({
      ...p,
      permissions: p.permissions.includes(perm)
        ? p.permissions.filter(x => x !== perm)
        : [...p.permissions, perm],
    }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, permissions: [...ROLE_DEFAULTS.benutzer] });
    setShowPw(false);
    setModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    const perms = Array.isArray(u.permissions) && u.permissions.length > 0
      ? u.permissions
      : ROLE_DEFAULTS[u.role] || ROLE_DEFAULTS.benutzer;
    setForm({
      name:        u.name || '',
      email:       u.email || '',
      password:    '',
      role:        u.role || 'benutzer',
      permissions: perms,
    });
    setShowPw(false);
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim())  { toast.error('Name ist Pflicht'); return; }
    if (!form.email.trim()) { toast.error('E-Mail ist Pflicht'); return; }
    if (!editing && !form.password) { toast.error('Passwort ist bei neuen Benutzern Pflicht'); return; }

    setSaving(true);
    try {
      const body = {
        name:        form.name.trim(),
        email:       form.email.trim(),
        role:        form.role,
        permissions: form.permissions,
      };
      if (form.password) body.password = form.password;

      if (editing) {
        await api.put(`/users/${editing.id}`, body);
        toast.success('Benutzer aktualisiert');
      } else {
        await api.post('/users', body);
        toast.success('Benutzer angelegt');
      }
      setModal(false);
      load();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`${u.name} wirklich löschen?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('Gelöscht');
      load();
    } catch(err) { toast.error(err.response?.data?.error || 'Fehler'); }
  };

  const getRoleInfo = (role) => ROLES.find(r => r.v === role) || ROLES[1];

  const isAdmin = me?.role === 'admin';

  return (
    <Layout title="Benutzerverwaltung">
      {/* Header */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16 }}>
        <div style={{ fontSize:'.82rem',color:'var(--text-muted)' }}>
          {users.length} Benutzer
        </div>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>
          <Plus size={14}/> Neuer Benutzer
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="loading-box"><div className="spinner spinner-lg"/></div>
      ) : users.length === 0 ? (
        <div className="card">
          <div className="empty-box">
            <h3>Keine Benutzer</h3>
            <button className="btn btn-primary" style={{marginTop:14}} onClick={openCreate}><Plus size={14}/> Anlegen</button>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
          {users.map(u => {
            const ri   = getRoleInfo(u.role);
            const perms = Array.isArray(u.permissions) ? u.permissions : ROLE_DEFAULTS[u.role] || [];
            return (
              <div key={u.id} className="card" style={{ padding:'14px 16px' }}>
                <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                  {/* Avatar */}
                  <div style={{
                    width:44,height:44,borderRadius:'50%',flexShrink:0,
                    background:u.role==='admin'?'linear-gradient(135deg,#e63946,#c1121f)':'linear-gradient(135deg,#2563eb,#1d4ed8)',
                    display:'flex',alignItems:'center',justifyContent:'center',
                    color:'#fff',fontSize:'.9rem',fontWeight:800,
                  }}>
                    {(u.name||'U').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontWeight:700,fontSize:'.92rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{u.name}</div>
                    <div style={{ fontSize:'.75rem',color:'var(--text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{u.email}</div>
                    <div style={{ marginTop:5,display:'flex',gap:5,flexWrap:'wrap' }}>
                      <span className={`badge ${ri.badge}`}>{ri.l}</span>
                      {u.role !== 'admin' && perms.filter(p => ['email_manage','news_manage','benutzer_manage'].includes(p)).map(p => {
                        const pi = ALL_PERMISSIONS.find(x => x.v === p);
                        return pi ? <span key={p} className="badge badge-purple" style={{fontSize:'.62rem'}}>{pi.l}</span> : null;
                      })}
                    </div>
                  </div>

                  {/* Aktionen */}
                  <div style={{ display:'flex',gap:6,flexShrink:0 }}>
                    <button className="btn btn-secondary btn-sm" style={{padding:'7px 10px'}} onClick={()=>openEdit(u)}>
                      <Edit2 size={14}/>
                    </button>
                    {u.id !== me?.id && (
                      <button className="btn btn-danger btn-sm" style={{padding:'7px 10px'}} onClick={()=>del(u)}>
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                </div>

                {/* Permissions-Übersicht */}
                {u.role !== 'admin' && (
                  <div style={{ marginTop:10,paddingTop:10,borderTop:'1px solid var(--border)' }}>
                    <div style={{ fontSize:'.62rem',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:5 }}>Freigegebene Bereiche</div>
                    <div style={{ display:'flex',flexWrap:'wrap',gap:4 }}>
                      {perms.map(p => {
                        const pi = ALL_PERMISSIONS.find(x => x.v === p);
                        return pi ? (
                          <span key={p} style={{ padding:'2px 7px',borderRadius:999,background:'var(--blue-bg)',color:'var(--blue)',fontSize:'.63rem',fontWeight:600 }}>
                            {pi.l}
                          </span>
                        ) : null;
                      })}
                      {perms.length === 0 && <span style={{fontSize:'.72rem',color:'var(--text-muted)',fontStyle:'italic'}}>Keine Berechtigungen</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <BottomSheet
          title={editing ? `${editing.name} bearbeiten` : 'Neuer Benutzer'}
          onClose={() => setModal(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" style={{flex:1,justifyContent:'center'}} onClick={()=>setModal(false)}>Abbrechen</button>
              <button type="button" className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={save} disabled={saving}>
                {saving?<><span className="spinner" style={{width:14,height:14,borderWidth:2}}/> Speichern...</>:'Speichern'}
              </button>
            </>
          }>
          <div style={{ display:'flex',flexDirection:'column',gap:14 }}>

            {/* Name */}
            <div className="f-group">
              <label className="f-label">Name *</label>
              <input className="f-input" value={form.name} onChange={e=>f('name',e.target.value)} placeholder="Max Mustermann" required/>
            </div>

            {/* E-Mail */}
            <div className="f-group">
              <label className="f-label">E-Mail *</label>
              <input className="f-input" type="email" value={form.email} onChange={e=>f('email',e.target.value)} placeholder="max@example.de" required autoCapitalize="none"/>
            </div>

            {/* Passwort */}
            <div className="f-group">
              <label className="f-label">{editing ? 'Passwort (leer = unverändert)' : 'Passwort *'}</label>
              <div style={{position:'relative'}}>
                <input
                  className="f-input"
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={e=>f('password',e.target.value)}
                  placeholder={editing ? 'Leer lassen für keine Änderung' : 'Mindestens 6 Zeichen'}
                  required={!editing}
                  style={{paddingRight:42}}
                />
                <button type="button" onClick={()=>setShowPw(!showPw)} style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',display:'flex',alignItems:'center'}}>
                  {showPw?<EyeOff size={16}/>:<Eye size={16}/>}
                </button>
              </div>
            </div>

            {/* Rolle — nur Admin darf ändern */}
            <div className="f-group">
              <label className="f-label">Rolle</label>
              {isAdmin ? (
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                  {ROLES.map(r => (
                    <button key={r.v} type="button"
                      onClick={() => {
                        f('role', r.v);
                        f('permissions', [...(ROLE_DEFAULTS[r.v] || [])]);
                      }}
                      style={{
                        padding:'10px 6px',borderRadius:9,border:'2px solid',
                        borderColor:form.role===r.v?'var(--accent)':'var(--border)',
                        background:form.role===r.v?'var(--accent-bg)':'var(--bg)',
                        cursor:'pointer',textAlign:'center',transition:'all 150ms',
                      }}>
                      <div style={{fontSize:'.85rem',fontWeight:700,color:form.role===r.v?'var(--accent)':'var(--text)'}}>{r.l}</div>
                      <div style={{fontSize:'.68rem',color:'var(--text-muted)',marginTop:2}}>{r.desc}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{padding:'10px 14px',borderRadius:9,background:'var(--bg)',border:'1px solid var(--border)',fontSize:'.85rem',color:'var(--text-muted)',display:'flex',alignItems:'center',gap:8}}>
                  <span>🔒</span><span>{ROLES.find(r=>r.v===form.role)?.l} — nur Admins dürfen Rollen ändern</span>
                </div>
              )}
            </div>

            {/* Berechtigungen — nur wenn nicht Admin-Rolle */}
            {form.role !== 'admin' && (
              <div className="f-group">
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                  <label className="f-label" style={{margin:0}}>Berechtigungen</label>
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => f('permissions', [...(ROLE_DEFAULTS[form.role] || [])])}>
                    Standard
                  </button>
                </div>

                {/* Permissions nach Gruppe */}
                {Object.entries(PERM_GROUPS).map(([group, groupPerms]) => (
                  <div key={group} style={{marginBottom:12}}>
                    <div style={{fontSize:'.65rem',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>{group}</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                      {groupPerms.map(p => {
                        const active = form.permissions.includes(p.v);
                        return (
                          <button key={p.v} type="button" onClick={()=>togglePerm(p.v)}
                            style={{
                              display:'flex',alignItems:'center',gap:8,
                              padding:'8px 10px',borderRadius:7,border:'1.5px solid',
                              borderColor:active?'var(--green)':'var(--border)',
                              background:active?'var(--green-bg)':'var(--bg-card)',
                              cursor:'pointer',textAlign:'left',
                              transition:'all 150ms',fontFamily:'inherit',
                            }}>
                            <div style={{
                              width:16,height:16,borderRadius:4,flexShrink:0,
                              background:active?'var(--green)':'transparent',
                              border:`2px solid ${active?'var(--green)':'var(--border)'}`,
                              display:'flex',alignItems:'center',justifyContent:'center',
                              transition:'all 150ms',
                            }}>
                              {active&&<Check size={10} color="#fff" strokeWidth={3}/>}
                            </div>
                            <span style={{fontSize:'.78rem',fontWeight:600,color:active?'var(--green)':'var(--text)'}}>{p.l}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {form.role === 'admin' && (
                  <div style={{padding:'12px 14px',background:'var(--red-bg)',borderRadius:10,border:'1px solid rgba(230,57,70,.2)',fontSize:'.78rem',color:'var(--accent)',display:'flex',alignItems:'center',gap:8}}>
                    <Shield size={14}/> Administratoren haben automatisch alle Berechtigungen.
                  </div>
                )}
              </div>
            )}
          </div>
        </BottomSheet>
      )}
    </Layout>
  );
}
